
// --- Vue & Template imports ---
import { Component, Vue} from 'vue-property-decorator';

// --- Models ---
import { DriverRoute } from '@/models/Driver.model';

// --- Services ---
import AppService from '@/services/app';
import DriverRouteService from '@/services/driverRoute';

@Component
export default class DriverRouteListing extends Vue {
  // used by Filter Area
  search: string = '';

  // used by No Data Slot
  isLoading: boolean = true;

  // used by Item Dialog for Create/Update
  showItemDialog: boolean = false;
  valid: boolean = false;
  title: string = '';
  clickedItem: DriverRoute = {
    id: 0,
    from: '',
    to: '',
    km: 0,
  };

  clicked: boolean = false;

  // used by Confirm Action Dialog for Delete
  action: string = '';
  dialogContent: string = '';
  showConfirmActionDialog: boolean = false;
  confirmActionDelay: boolean = false;

  rules = {
    required: (value: string) => !!value || '',
  };

  // #region Functions to display list data on page load
    headers() {
      return [
        {
          text: 'From',
          value: 'from',
        },
        {
          text: 'To',
          value: 'to',
        },
        {
          text: 'KM',
          value: 'km',
        },
        {
          sortable: false,
          text: '',
          value: 'action',
          align: 'end',
        },
      ];
    }

    async beforeMount() {
      await this.listAll();
    }

    async listAll() {
      try {
        this.isLoading = true;
        const response: string = await DriverRouteService.listAll(true);

        if (response.length > 0 && response !== 'No Data Found') {
          AppService.errorHandler(response);
        }

      } finally {
        this.isLoading = false;
      }
    }

    allowAddNewFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
      ]);
    }

    allowEditFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
      ]);
    }

    allowDeleteFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
      ]);
    }
  // #endregion

  // #region Functions required for the Item Dialog for Create/Update
    async launchNewItemDialog(item: DriverRoute) {
      this.clickedItem = {
        id: 0,
        from: '',
        to: '',
        km: 0,
      };

      this.valid = false;
      this.title = 'Create New Driver Route';

      this.showItemDialog = true;
    }

    async launchUpdateItemDialog(item: DriverRoute) {
      this.clickedItem = Object.assign({}, item);

      this.valid = true;
      this.title = 'Update Driver Route';
      this.showItemDialog = true;
    }

    doCreateUpdate() {
      if (this.clickedItem.id <= 0) {
        this.doCreate();
      } else {
        this.doUpdate();
      }
    }

    async doCreate() {
      try {
        this.clicked = true;

        const response = await DriverRouteService.create({
          from: this.clickedItem.from,
          to: this.clickedItem.to,
          km: this.clickedItem.km,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listAll();
            this.showItemDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('DriverRoutes.vue - doCreate - 401 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.clicked = false;
      }
    }

    async doUpdate() {
      try {
        this.clicked = true;

        const response = await DriverRouteService.update({
          id: this.clickedItem.id,
          from: this.clickedItem.from,
          to: this.clickedItem.to,
          km: this.clickedItem.km,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listAll();
            this.showItemDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('DriverRoutes.vue - doUpdate - 438 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.clicked = false;
      }
    }
  // #endregion

  // #region Functions required for the Confirm Action Dialog for Delete
    async launchDeleteItemDialog(item: DriverRoute) {
      this.clickedItem = Object.assign({}, item);

      this.action = 'DELETE';
      this.dialogContent = 'You are about to <span style="font-weight: 700;color: #C01212">delete</span> ' 
        + ' the Driver Route from ' + this.clickedItem.from + ' to ' + this.clickedItem.to;
      this.showConfirmActionDialog = true;
    }

    ConfirmActionDialog_cancel() {
      this.showConfirmActionDialog = false;
    }

    async ConfirmActionDialog_doConfirmed() {
      if (this.action === 'DELETE') {
        await this.doDelete();
      }
    }

    async doDelete() {
      try {
        this.confirmActionDelay = true;

        const response = await DriverRouteService.delete({
          id: this.clickedItem.id,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listAll();
            this.showConfirmActionDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('DriverRoutes.vue - doDelete - 493 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.confirmActionDelay = false;
      }
    }
  // #endregion

}
